import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { MenuService } from '../../../../services/menu/menu.service';
import { Subscription } from 'rxjs';
import { UserInfoService } from '../../../../services/security/user-info.service';
import { SwitchRoleComponent } from './modals/switch-role/switch-role.component';
import { MatDialog } from '@angular/material/dialog';
import { ReleaseNotes } from '../../../../configuration/models/release-notes.model';
import { ReleaseNotesComponent } from '../../../modal/release-notes/release-notes.component';
import { ReleaseNotesService } from '../../../../configuration/services/release-notes.service';
import { NotificationService } from '../../../../services/alerte/notification.service';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton } from '@angular/material/button';
import { ProfilComponent } from '../profil/profil.component';
import { MatSidenavContainer, MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { MenuNavigationComponent } from '../../../../menu/menu-navigation/menu-navigation.component';
import { BreadcrumbComponent } from '../../../../commun/breadcrumb/breadcrumb.component';
import { RouterOutlet } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { NgIf, NgClass } from '@angular/common';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    NgIf,
    NgClass,
    ProfilComponent,
    MatSidenavContainer,
    MatSidenav,
    MenuNavigationComponent,
    MatSidenavContent,
    BreadcrumbComponent,
    RouterOutlet
  ]
})

/**
 * Ce composant est utilisé dans app.component.html
 */
export class MainLayoutComponent implements OnInit, OnDestroy {
  @Input()
  titre = '';
  @Input()
  srcLogo = '/assets-socle/svg/logoApp.svg';
  @Input()
  srcLogoDepartement = 'assets-socle/svg/departement77.svg';
  @Input()
  bgColorPage = 'white';

  public numVersionApp = '0.0.0';

  /**
   * Config du menu
   */
  public isMenuOpen = false;
  private toggleMenu = new Subscription();

  public sessionMinute: number = 60;
  public sessionSeconde: number = 60;
  public roleSession: any | null = null;
  public switchedRole: string | null = null;
  public isAdmin: boolean = false;
  public switchRoleAvailable: boolean = false;

  constructor(
    private readonly menuService: MenuService,
    private readonly userInfoService: UserInfoService,
    private readonly dialog: MatDialog,
    private readonly releaseNotesService: ReleaseNotesService,
    private readonly notificationService: NotificationService
  ) {}

  async ngOnInit(): Promise<void> {
    this.numVersionApp = environment.numVersionApp;

    await this.userInfoService.afterLoadUserInfo().then(async (): Promise<void> => {
      this.isAdmin = this.userInfoService.isAdministrateur(true);
      this.switchRoleAvailable = this.isAdmin && !environment.production;
      if (this.isAdmin) {
        this.findSwitchRole();
      } else {
        this.exitRoleView();
      }

      this.checkReleaseNotes();
    });

    this.toggleMenu = this.menuService.toggleMenu.subscribe((): void => {
      this.toggleDrawer();
    });
  }

  private checkReleaseNotes(): void {
    if (!this.releaseNotesService.aVuVersionActuelle()) {
      const versionActuelle = this.releaseNotesService.obtenirVersionActuelle();
      this.releaseNotesService.obtenirNotesVersion(versionActuelle).subscribe(
        (releaseNote: ReleaseNotes) => {
          this.dialog
            .open(ReleaseNotesComponent, {
              maxWidth: '600px',
              width: '90vw',
              data: releaseNote,
              disableClose: true,
              panelClass: ['custom-dialog-container', 'blueMode', 'release-notes-dialog']
            })
            .afterClosed()
            .subscribe(() => {
              this.releaseNotesService.enregistrerVersionActuelle();
            });
        },
        error => {
          this.notificationService.error(error.error.message, 'Erreur');
        }
      );
    }
  }

  toggleDrawer(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  ngOnDestroy(): void {
    this.toggleMenu.unsubscribe();
  }

  exitRoleView(reloadPage: boolean = false): void {
    this.switchedRole = null;
    this.sessionMinute = 60;
    this.sessionSeconde = 60;
    localStorage.removeItem('_switched_role');
    localStorage.removeItem('_switched_timestamp');
    clearInterval(this.roleSession);
    if (reloadPage) {
      // Reload de la page pour que le rôle soit bien retiré de partout
      window.location.reload();
    }
  }

  findSwitchRole(): void {
    const role = localStorage.getItem('_switched_role');
    const roleTimestamp = localStorage.getItem('_switched_timestamp');
    if (!role) {
      return;
    }
    if (!roleTimestamp) {
      return;
    }
    this.switchedRole = role;

    this.startCountDown(parseInt(roleTimestamp));
  }

  getElapsedTime(switchTimestamp: number): number {
    return (Date.now() - switchTimestamp) / 60000;
  }

  initCountDownValues(switchTimestamp: number): void {
    this.sessionMinute = Math.trunc(60 - this.getElapsedTime(switchTimestamp));
    const remainingSecondes = 60 * parseFloat((this.getElapsedTime(switchTimestamp) % 1).toFixed(2));
    this.sessionSeconde = 60 - Math.trunc(remainingSecondes);
  }

  startCountDown(switchTimestamp: number): void {
    if (this.getElapsedTime(switchTimestamp) >= 60) {
      this.exitRoleView(true);
      return;
    }
    this.initCountDownValues(switchTimestamp);
    this.roleSession = setInterval((): void => {
      const elapsedMinutes = this.getElapsedTime(switchTimestamp);
      // Mise à jour des minutes restantes si besoin
      const remainingMinutes = Math.trunc(60 - elapsedMinutes);
      if (this.sessionMinute >= remainingMinutes) {
        this.sessionMinute = remainingMinutes;
      }
      // Mise à jour des secondes
      this.sessionSeconde = this.sessionSeconde === 0 ? 59 : this.sessionSeconde - 1;

      if (elapsedMinutes >= 60) {
        this.sessionSeconde = 60;
        localStorage.removeItem('_switched_timestamp');
        clearInterval(this.roleSession);
      }
    }, 1000);
  }

  openSwitchRole(): void {
    this.dialog
      .open(SwitchRoleComponent, {
        data: {}
      })
      .afterClosed()
      .subscribe((selectedRole: string): void => {
        if (!selectedRole) {
          return;
        }
        this.switchedRole = selectedRole;
        localStorage.setItem('_switched_role', selectedRole);
        localStorage.setItem('_switched_timestamp', Date.now().toString());
        this.findSwitchRole();
        // Reload de la page pour que le rôle soit bien pris en compte par tous les composants
        window.location.reload();
      });
  }

  afficherNotesDeVersion(): void {
    this.releaseNotesService.obtenirNotesVersion(this.numVersionApp).subscribe(
      (releaseNote: ReleaseNotes) => {
        this.dialog.open(ReleaseNotesComponent, {
          maxWidth: '600px',
          width: '90vw',
          data: releaseNote,
          disableClose: false,
          panelClass: ['custom-dialog-container', 'blueMode', 'release-notes-dialog']
        });
      },
      error => {
        this.notificationService.error(error.error.message, 'Erreur');
      }
    );
  }

  protected readonly environment = environment;
}
